/**
 * Kids Badges Gallery
 *
 * Page for children to view their earned and available badges.
 * Badges grouped by category with celebration on new unlock.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChild } from '@/contexts/ChildContext';
import { useChildBadges, useAcknowledgeBadges } from '@/hooks/queries/useGamification';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingState } from '@/components/shared/feedback/LoadingState';
import { EmptyState } from '@/components/shared/feedback/EmptyState';
import { ArrowLeft, Award, Sparkles, Star, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BadgeEarned, BadgeDefinition } from '@/services/gamification';
import Confetti from 'react-confetti';
import yolunoLogo from '@/assets/landing/yoluno-logo-original.png';

const CATEGORY_LABELS: Record<string, { label: string; emoji: string }> = {
  streak: { label: 'Streak', emoji: '🔥' },
  learning: { label: 'Learning', emoji: '📚' },
  story: { label: 'Story', emoji: '📖' },
  journey: { label: 'Journey', emoji: '🗺️' },
  special: { label: 'Special', emoji: '⭐' },
  family: { label: 'Family', emoji: '👨‍👩‍👧‍👦' },
  mood: { label: 'Mood', emoji: '😊' },
};

// Badge name to emoji mapping (since SVG files don't exist)
const BADGE_EMOJIS: Record<string, string> = {
  first_chat: '💬',
  chat_explorer: '🗣️',
  chat_master: '🏆',
  storyteller: '📝',
  story_collector: '📚',
  story_master: '🌟',
  journey_starter: '🚀',
  journey_finisher: '🏁',
  journey_explorer: '🧭',
  streak_3: '🔥',
  streak_7: '⚡',
  streak_14: '💪',
  streak_30: '👑',
  points_100: '⭐',
  points_500: '🌟',
  points_1000: '💫',
  points_5000: '🏅',
  family_first: '👨‍👩‍👧',
  family_explorer: '👨‍👩‍👧‍👦',
  family_champion: '🏠',
  mood_first: '😊',
  mood_explorer: '🌈',
  mood_master: '🧘',
};

function getBadgeEmoji(badge: BadgeDefinition): string {
  return BADGE_EMOJIS[badge.name] || CATEGORY_LABELS[badge.category]?.emoji || '🏅';
}

function getRequirementHint(badge: BadgeDefinition): string {
  const val = badge.requirement_value;
  switch (badge.requirement_type) {
    case 'streak':
      return `Be active for ${val} days in a row!`;
    case 'points':
      return `Earn ${val.toLocaleString()} points by completing activities!`;
    case 'activity_count':
      if (badge.category === 'learning') return `Send ${val} message${val > 1 ? 's' : ''} to Luno in chat!`;
      if (badge.category === 'story') return `Create ${val} stor${val > 1 ? 'ies' : 'y'}!`;
      if (badge.category === 'journey') return `Complete ${val} learning journey${val > 1 ? 's' : ''}!`;
      if (badge.category === 'family') return `Add ${val} family member${val > 1 ? 's' : ''} to your album!`;
      if (badge.category === 'mood') return `Check in with your mood ${val} time${val > 1 ? 's' : ''}!`;
      return `Complete this activity ${val} time${val > 1 ? 's' : ''}!`;
    case 'specific_action':
      return badge.description;
    default:
      return badge.description;
  }
}

export function KidsBadgesPage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { activeChild } = useChild();

  const effectiveChildId = childId || activeChild?.id;

  const { data: badgesData, isLoading } = useChildBadges(effectiveChildId);
  const acknowledgeBadges = useAcknowledgeBadges();

  const [celebratingBadge, setCelebratingBadge] = useState<BadgeDefinition | null>(null);
  const [lockedBadge, setLockedBadge] = useState<BadgeDefinition | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  const earned = badgesData?.earned ?? [];
  const available = badgesData?.available ?? [];
  const earnedBadgeIds = new Set(earned.map((e) => e.badge_definition_id));

  // Auto-celebrate unnotified badges
  useEffect(() => {
    const unnotified = earned.filter((e) => !e.notified);
    if (unnotified.length > 0 && !celebratingBadge) {
      setCelebratingBadge(unnotified[0].badge);
      setShowConfetti(true);
      acknowledgeBadges.mutate({
        childId: effectiveChildId!,
        badgeIds: unnotified.map((e) => e.badge_definition_id),
      });
    }
  }, [earned, celebratingBadge, effectiveChildId, acknowledgeBadges]);

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  // Group all badges by category
  const allBadges = available.map((badge) => ({
    badge,
    earned: earned.find((e) => e.badge_definition_id === badge.id),
  }));

  const categories = Object.entries(
    allBadges.reduce(
      (acc, item) => {
        const cat = item.badge.category;
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
      },
      {} as Record<string, typeof allBadges>
    )
  ).sort(([a], [b]) => {
    const order = ['streak', 'learning', 'story', 'journey', 'family', 'mood', 'special'];
    return order.indexOf(a) - order.indexOf(b);
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-kids-gradient flex items-center justify-center">
        <LoadingState message="Loading your badges..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kids-gradient">
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
          colors={['#FFD700', '#FFA500', '#FF6347', '#9370DB', '#00CED1']}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(`/kids/${childId}`)}
            className="flex items-center justify-center hover:opacity-80 transition-opacity shrink-0"
            aria-label="Home"
          >
            <img src={yolunoLogo} alt="Yoluno" style={{ height: 36, objectFit: 'contain' }} />
          </button>
          <div className="flex-1">
            <h1 className="text-body-lg font-display font-bold">Badges you've earned</h1>
            <p className="text-body-sm text-muted-foreground">
              {earned.length} of {available.length} earned
            </p>
          </div>
          <div className="flex items-center gap-1.5 rounded-full bg-lala/10 px-3 py-1.5">
            <Award className="h-4 w-4 text-lala" />
            <span className="text-body-sm font-bold text-lala">{earned.length}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        {categories.length === 0 ? (
          <EmptyState
            icon={Award}
            title="Your first badge is just around the corner"
            description="Every adventure earns you something special — keep exploring!"
            className="py-12"
          />
        ) : (
          categories.map(([category, badges]) => {
            const catInfo = CATEGORY_LABELS[category] || { label: category, emoji: '🏅' };
            return (
              <div key={category}>
                <h2 className="text-body-lg font-display font-bold mb-3 flex items-center gap-2">
                  <span>{catInfo.emoji}</span>
                  {catInfo.label}
                </h2>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {badges.map(({ badge, earned: earnedData }) => {
                    const isEarned = !!earnedData;
                    return (
                      <Card
                        key={badge.id}
                        className={cn(
                          'overflow-hidden cursor-pointer transition-all',
                          isEarned
                            ? 'hover:shadow-lg hover:scale-105'
                            : 'opacity-50 grayscale'
                        )}
                        onClick={() => {
                          if (isEarned) {
                            setCelebratingBadge(badge);
                          } else {
                            setLockedBadge(badge);
                          }
                        }}
                      >
                        <CardContent className="p-3 text-center">
                          <div className="relative mx-auto w-16 h-16 mb-2 flex items-center justify-center rounded-full bg-gradient-to-br from-lala/10 to-gold/10">
                            <span className="text-h3 leading-none">{getBadgeEmoji(badge)}</span>
                            {!isEarned && (
                              <Lock className="absolute bottom-0 right-0 h-4 w-4 text-muted-foreground bg-white rounded-full p-0.5" />
                            )}
                          </div>
                          <h3 className="font-medium text-caption line-clamp-2">
                            {badge.display_name}
                          </h3>
                          {isEarned && earnedData && (
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {new Date(earnedData.earned_at).toLocaleDateString()}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Celebration Dialog */}
      <Dialog open={!!celebratingBadge} onOpenChange={() => setCelebratingBadge(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-h4 font-display flex items-center justify-center gap-2">
              <Star className="h-6 w-6 text-lala fill-lala" />
              You earned a badge!
              <Star className="h-6 w-6 text-lala fill-lala" />
            </DialogTitle>
          </DialogHeader>

          {celebratingBadge && (
            <div className="space-y-4 text-center">
              <div className="mx-auto w-24 h-24 flex items-center justify-center rounded-full bg-gradient-to-br from-lala/10 to-gold/10">
                <span className="text-h1 leading-none">{getBadgeEmoji(celebratingBadge)}</span>
              </div>
              <div>
                <h3 className="text-body-lg font-bold">{celebratingBadge.display_name}</h3>
                <p className="text-muted-foreground mt-1">{celebratingBadge.description}</p>
              </div>
              <Button onClick={() => setCelebratingBadge(null)} className="w-full" size="lg">
                <Sparkles className="h-5 w-5 mr-2" />
                Awesome!
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Locked Badge Dialog */}
      <Dialog open={!!lockedBadge} onOpenChange={() => setLockedBadge(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-h4 font-display flex items-center justify-center gap-2">
              <Lock className="h-6 w-6 text-muted-foreground" />
              Not yet...
            </DialogTitle>
          </DialogHeader>

          {lockedBadge && (
            <div className="space-y-4 text-center">
              <div className="mx-auto w-24 h-24 flex items-center justify-center rounded-full bg-gradient-to-br from-muted to-muted opacity-60 grayscale">
                <span className="text-h1 leading-none">{getBadgeEmoji(lockedBadge)}</span>
              </div>
              <div>
                <h3 className="text-body-lg font-bold">{lockedBadge.display_name}</h3>
                <p className="text-muted-foreground mt-1">{lockedBadge.description}</p>
              </div>
              <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
                <p className="text-body-sm font-semibold text-primary mb-1">How to earn this one:</p>
                <p className="text-body-sm text-muted-foreground">{getRequirementHint(lockedBadge)}</p>
              </div>
              <Button onClick={() => setLockedBadge(null)} variant="secondary" className="w-full" size="lg">
                Got it!
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
